/**
 * Read-only view of a `Both` mapping. Operators cannot edit this row directly:
 * the system regenerates it whenever the matching Anverso/Reverso rows change.
 * Each rule shows where it came from and a link back to the source row.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslate, useRecordContext } from 'react-admin';
import { Alert, AlertTitle, Box, Button, Grid, Stack, Typography } from '@mui/material';
import { OpenInNewRounded as OpenInNew } from '@mui/icons-material';
import { FormSection } from '@/components/shell';
import { httpClient } from '@/utils/httpClient';
import { DOCUMENT_SIDE_TO_INT } from '@/config/constants';
import { BothRowBadge } from './BothRowBadge';
import { RuleSummary, sourceName } from './RuleSummary';

const BACK_TO = '/kyc-config?tab=fields-mapping';

interface MappingRow {
  id: string;
  documentSide: string | number;
  documentTypeResponse?: { id: string; name?: string };
  nationalityResponse?: { id: string; name?: string };
  mappingFields?: Array<{
    fieldName?: string;
    standardFieldName?: string;
    regex?: string;
    kycMappingField?: Array<{
      kycField?: { id: string; name: string };
      prefix?: string;
      suffix?: string;
    }>;
  }>;
}

const sideAsInt = (side: string | number): number =>
  typeof side === 'number' ? side : (DOCUMENT_SIDE_TO_INT[side] ?? 0);

const ReadOnlyValue: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Box sx={{ mt: 0.25 }}>{children}</Box>
  </Box>
);

export const BothReadOnlyView: React.FC = () => {
  const t = useTranslate();
  const navigate = useNavigate();
  const record = useRecordContext<MappingRow>();
  const [siblings, setSiblings] = useState<{ front?: MappingRow; back?: MappingRow }>({});

  // Fetch all mappings for this (DocumentType, Nationality) so we can link
  // each rule back to its origin Anverso/Reverso row.
  useEffect(() => {
    if (!record) return;
    const docTypeId = record.documentTypeResponse?.id;
    const nationalityId = record.nationalityResponse?.id;
    if (!docTypeId || !nationalityId) return;
    httpClient
      .get<MappingRow[] | { data?: MappingRow[] }>(
        `/admin/fields-mapping/GetMappingFields?draw=1&length=10000&start=0&search=&column=&orderBy=asc&DocumentTypeId=${docTypeId}&NationalityId=${nationalityId}`,
      )
      .then((r) => {
        const rows: MappingRow[] = Array.isArray(r.data) ? r.data : r.data?.data || [];
        const front = rows.find((row) => sideAsInt(row.documentSide) === 1);
        const back = rows.find((row) => sideAsInt(row.documentSide) === 2);
        setSiblings({ front, back });
      })
      .catch(() => {
        /* non-blocking: links will fall back to the listing */
      });
  }, [record]);

  if (!record) return null;

  const findOrigin = (
    fieldName: string,
  ): { side: 'Front' | 'Back'; recordId: string } | null => {
    const matchIn = (sibling?: MappingRow): boolean =>
      Boolean(
        sibling?.mappingFields?.some(
          (f) => (f.fieldName || f.standardFieldName) === fieldName,
        ),
      );
    if (matchIn(siblings.front) && siblings.front?.id)
      return { side: 'Front', recordId: siblings.front.id };
    if (matchIn(siblings.back) && siblings.back?.id)
      return { side: 'Back', recordId: siblings.back.id };
    return null;
  };

  const goToSource = (mappingId: string) => {
    navigate(`/fields-mapping/${mappingId}`);
  };

  const fields = record.mappingFields || [];

  return (
    <Stack spacing={3} sx={{ p: { xs: 2, sm: 3 } }}>
      <Alert severity="info">
        <AlertTitle>{t('fields-mapping.both.auto_managed_banner_title')}</AlertTitle>
        {t('fields-mapping.both.auto_managed_banner')}
        {(siblings.front?.id || siblings.back?.id) && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            {siblings.front?.id && (
              <Button variant="outlined" startIcon={<OpenInNew />} onClick={() => goToSource(siblings.front!.id)}>
                {t('fields-mapping.both.open_front')}
              </Button>
            )}
            {siblings.back?.id && (
              <Button variant="outlined" startIcon={<OpenInNew />} onClick={() => goToSource(siblings.back!.id)}>
                {t('fields-mapping.both.open_back')}
              </Button>
            )}
          </Stack>
        )}
      </Alert>

      <FormSection title={t('screens.fieldMappings.documentInfo')}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <ReadOnlyValue label={t('screens.fieldMappings.documentType')}>
              <Typography variant="body2">{record.documentTypeResponse?.name || '—'}</Typography>
            </ReadOnlyValue>
          </Grid>
          <Grid item xs={12} sm={4}>
            <ReadOnlyValue label={t('screens.fieldMappings.nationality')}>
              <Typography variant="body2">{record.nationalityResponse?.name || '—'}</Typography>
            </ReadOnlyValue>
          </Grid>
          <Grid item xs={12} sm={4}>
            <ReadOnlyValue label={t('screens.fieldMappings.documentSide')}>
              <BothRowBadge />
            </ReadOnlyValue>
          </Grid>
        </Grid>
      </FormSection>

      <FormSection title={t('screens.fieldMappings.mappingFields')}>
        {fields.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('fields-mapping.both.empty_rules')}
          </Typography>
        )}
        {fields.length > 0 && (
          <Box>
            {fields.map((f, i) => {
              const fieldName = f.fieldName || f.standardFieldName || '';
              const origin = findOrigin(fieldName);
              return (
                <Stack
                  key={`${fieldName}-${i}`}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ py: 0.5, borderTop: i > 0 ? 1 : 0, borderColor: 'divider', minWidth: 0 }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <RuleSummary
                      output={fieldName}
                      sources={(f.kycMappingField || []).map((k) => sourceName(k) || '—')}
                      regex={f.regex}
                    />
                  </Box>
                  {origin && (
                    <Button
                      variant="text"
                      endIcon={<OpenInNew />}
                      onClick={() => goToSource(origin.recordId)}
                      aria-label={`${t('fields-mapping.both.see_source')}: ${t(origin.side === 'Front' ? 'fields-mapping.both.side_front' : 'fields-mapping.both.side_back')}`}
                      sx={{ flexShrink: 0 }}
                    >
                      {t(origin.side === 'Front' ? 'fields-mapping.both.side_front' : 'fields-mapping.both.side_back')}
                    </Button>
                  )}
                </Stack>
              );
            })}
          </Box>
        )}
      </FormSection>

      <Box>
        <Button variant="outlined" onClick={() => navigate(BACK_TO)}>{t('ra.action.back')}</Button>
      </Box>
    </Stack>
  );
};
